
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { toast } from '@/components/ui/use-toast';
import { isAuthError } from '@/lib/errors';

interface Props {
  children?: ReactNode;
  /**
   * Optional custom fallback UI to display when an error occurs
   * If not provided, the default error UI will be shown
   */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    
    
    const isAuth = isAuthError(error);
    
    toast({
      title: isAuth ? "Authentication Error" : "Error",
      description: isAuth ? "Please sign in to continue" : (error.message || "An unexpected error occurred"),
      variant: "destructive",
    });

    if (isAuth) {
      window.location.href = '/';
    }
  }

  public render() {
    if (this.state.hasError) {
      // If a custom fallback UI is provided, use it instead of the default
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      // Otherwise, use the default error UI
      const isAuth = isAuthError(this.state.error);
      
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-xl font-semibold mb-4">
              {isAuth ? "Authentication Required" : "Something went wrong"}
            </h2>
            <p className="text-gray-600 mb-4">
              {isAuth 
                ? "Please sign in to continue using the application."
                : "We're sorry, but there was an error loading this page."}
            </p>
            <button
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
              onClick={() => {
                this.setState({ hasError: false });
                window.location.href = isAuth ? '/' : window.location.href;
              }}
            >
              {isAuth ? "Sign In" : "Try again"}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
